import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { emitOnce } from '@ngneat/elf';
import { useObservable } from '@ngneat/react-rxjs';
import { auditsInformationService } from '@store/audits/audits-information/audit-information.service';
import {
  AuditInformation,
  AuditInformationCreatedFromStepEnum, AuditInformationTypeEnum, RepositionDTO
} from '@store/audits/audits-information/audits-information.model';
import { auditsInformationQuery } from '@store/audits/audits-information/audits-information.query';
import { auditsOperationQuery } from '@store/audits/audits-operation/audit-operation.query';
import { AuditOperationStepTypeEnum } from '@store/audits/audits-operation/audits-operation.model';
import { auditsOperationService } from '@store/audits/audits-operation/audits-operation.service';
import { templatesQuery } from '@store/audits/templates/templates.query';
import { sortByPosition } from '@utils/sortData';
import { useResponsive } from '@utils/useResponsive';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { finalize } from 'rxjs';
import AuditsContent from '../../components/AuditsContent';
import { useDragnDrop } from './utils/useDragnDrop';


interface AuditTabProps {
  isTemplate?: boolean;
}
const AuditTab = (props: AuditTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isTemplate } = props;

  const [{ auditsInformation }] = useObservable(auditsInformationQuery.auditsInformationList$);
  const [{ auditsOperation }] = useObservable(auditsOperationQuery.auditsOperationList$);

  const [{ auditTemplateDetails }] = useObservable(templatesQuery.auditTemplateDetails$);
  const [filters] = useObservable(auditsOperationQuery.filters$);

  const { auditId } = useParams();
  const [isDiseabled, setIsDiseabled] = useState<boolean>(true);
  const [isloading, setIsloading] = useState<boolean>(false);
  const [dragnDropItems, setDragnDropItems] = useState<any[]>([]);
  const { onDragEnd, getListStyle } = useDragnDrop();
  const { isTablets } = useResponsive();
  const { enqueueSnackbar } = useSnackbar();


  useEffect(() => {
    if (auditId) {
      emitOnce(() => {
        auditsInformationService.setStep(AuditInformationCreatedFromStepEnum.AUDIT);
        auditsOperationService.setStep(AuditOperationStepTypeEnum.AUDIT);
      });
      setIsloading(true);
      auditsInformationService.getAuditsInformation(auditId).subscribe();
      auditsOperationService.getAuditsInformationOP(auditId).pipe(
        finalize(() => setTimeout(() => setIsloading(false), 100))
      ).subscribe();
    }
    return () => {
      auditsInformationService.resetStore();
    };
  }, [auditId, filters]);

  useEffect(() => {
    if (auditsInformation && auditsOperation) {
      setDragnDropItems([...auditsInformation, ...auditsOperation.map(item => ({ ...item, isOperation: true }))].sort(sortByPosition));
    }
  }, [auditsInformation, auditsOperation]);

  const handleSavePosition = () => {
    if (!isDiseabled && auditId) {
      const positionData: RepositionDTO = {
        data: dragnDropItems.map((item, idx) => ({
          ressourceId: item.id,
          position: idx,
          isOperation: item?.isOperation ?? false
        })),
      };
      auditsInformationService.updatePosition(positionData, auditId).subscribe();
    }
    setIsDiseabled(!isDiseabled);
  };

  const handleExportCSV = () => {
    auditsOperationService.getAuditCSV(auditId!)
        .pipe()
        .subscribe({
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
  };


  return (
    <Stack flex={1} width="100%" py={'20px'} spacing={2} sx={{ backgroundColor: 'white' }} mt='20px' borderRadius='2px' mb='20px'>
      <Stack direction="row" spacing={2} alignItems='center' justifyContent='space-between' px={'20px'}>
        <Typography fontSize="20px" fontWeight="700">
          {t('audits.global.audit')}
        </Typography>
        {!isTemplate && (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="primary" onClick={handleExportCSV}>
              {t('global.exportCSV')}
            </Button>
            <Button variant="outlined" color="primary" onClick={handleSavePosition}>
              {isDiseabled ? t('audits.buttons.dragnDrop') : t('audits.buttons.save-position')}
            </Button>
            <Button variant="contained" color="primary" onClick={() => { navigate('add-data'); }}>
              {t('audits.buttons.add-data')}
            </Button>
          </Stack>
        )}
      </Stack>
      <DragDropContext onDragEnd={(res) => onDragEnd(res, dragnDropItems, setDragnDropItems)}>
        <Stack
          overflow="auto"
          height={'15rem'}
          py={'15px'}
          px="20px"
          flexGrow={1}
          my="8px"
          spacing={2}
          className="scrollable"
          sx={{
            alignItems: isloading ? 'center' : 'none',
            justifyContent: isloading ? 'center' : 'none'
          }}
        >
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <Stack
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
                sx={{ width: isTablets ? 'fit-content' : undefined }}
                spacing={2}
              >
                {isloading ?
                  <CircularProgress
                    size={20}
                    style={{ alignSelf: 'center' }}
                    thickness={10}
                  />
                  : !isTemplate && dragnDropItems ? (
                    dragnDropItems?.map((dragnDropItem, index) =>
                      <Draggable
                        key={dragnDropItem.id?.toString()}
                        draggableId={dragnDropItem.id?.toString()}
                        isDragDisabled={isDiseabled}
                        index={index}
                      >
                        {(provided) => (
                          <Stack
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              border: !isDiseabled ? '1px dashed #ccc' : 'none',
                            }}
                          >
                            {dragnDropItem.oldValue ?
                              <AuditsContent
                                key={dragnDropItem.id}
                                auditOperation={dragnDropItem}
                                isTemplate={isTemplate}
                                isDraggable={isDiseabled}
                                isAudit
                              />
                              : <AuditsContent
                                key={dragnDropItem.id}
                                auditInformation={dragnDropItem}
                                isTemplate={isTemplate}
                                isDraggable={isDiseabled} />
                            }
                          </Stack>
                        )}
                      </Draggable>

                    )
                  ) : (
                    <Typography color='#868F9C' fontSize="15px" fontWeight="100">
                      {!isTemplate ? t('audits.global.pre-audit-no-data') : null}
                    </Typography>
                  )}
                {isTemplate && auditTemplateDetails?.auditOperation?.length !== 0 && (
                  auditTemplateDetails?.auditOperation?.map((auditOperation) =>
                    <AuditsContent key={auditOperation.id} auditOperation={auditOperation} isTemplate={isTemplate} />
                  )
                )}
                {isTemplate && auditTemplateDetails?.auditInformationAudit?.length !== 0 && (
                  auditTemplateDetails?.auditInformationAudit?.map((auditInformation, index) => {
                    const auditIn: AuditInformation = {
                      id: 0,
                      type: auditInformation as AuditInformationTypeEnum,
                      auditId: '0',
                      createdFrom: AuditInformationCreatedFromStepEnum.AUDIT
                    };
                    return (<AuditsContent key={index} auditInformation={auditIn} isTemplate={isTemplate} />);
                  })
                )}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </Stack>
      </DragDropContext>
      <Outlet />
    </Stack >
  );
};

export default AuditTab;
